import React, { ReactNode, useContext, useEffect, useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled, { ThemeContext } from 'styled-components';
import type {
    NodeInstance,
    NodeInstanceState,
    NodeInstanceDataPort,
    DataType,
    DataConnection,
    FunctionDefinition,
} from '@glueapp/graph-types';
import { inherentInPortName, createOptional } from '@glueapp/graph-types';
import { createSingleNodeInstance, NODE_LIBRARY_DICTIONARY } from '@glueapp/graph-nodes';
import { OutPortList } from '@/GraphEditor/drawingLayer/nodeComposition/OutPortList';
import { NodeEventInsulator } from '@/GraphEditor/drawingLayer/graphComponents/NodeInterfaceEventInsulator';
import { useTriggerRun } from '@/GraphEditor/drawingLayer/contexts/TriggerRunContext';
import { useNodeSelected } from '@/GraphEditor/drawingLayer/contexts/NodeSelectedContext';
import { useMergeInGraph } from '@/GraphEditor/drawingLayer/contexts/MergeInGraphContext';
import { useNodeShape } from '@/GraphEditor/drawingLayer/contexts/NodeShapeContext';
import { useNavigateToFunction } from '@/GraphEditor/drawingLayer/contexts/NavigateToFunctionContext';
import { useGraphDispatch } from '@/GraphEditor/drawingLayer/contexts/GraphDispatchContext';
import { useFunctions } from '@/GraphEditor/drawingLayer/contexts/FunctionsContext';
import { RainbowBackgroundAnimation } from '@/base/GlueRainbowBackgroundAnimation';
import { ArrowIcon } from '@/base/ArrowIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useApiServerApi } from '@/api/useApiServerApi';
import { Copy, Check, Plus, X, Pencil, Terminal, ChevronRight, Shield, Download } from 'lucide-react';
import { InPortList } from '@/GraphEditor/drawingLayer/nodeComposition/InPortList';
import { ShapeTypeSelectorEditor } from '@/NodeInstanceEditor/ShapeValueEditor/ShapeValueEditor';
import { ThemeType } from '@/theme/glueTheme';
import { useTheme } from '@/theme/useTheme';
import { getEnvironmentConfig } from '@/environmentConfig';
import { createAuthFunction, type AuthMethod } from './createAuthFunction';
import { addFunction, deleteFunction, setFunctionPortValue } from '@/GraphEditor/actions';

// --- Types ---

interface EndpointDefinition {
    id: string;
    method: string;
    path: string;
    streaming: boolean;
    timeout: number;
    pathParams: { name: string; type: DataType }[];
    queryParams: { name: string; type: DataType; optional: boolean }[];
    requestBodyShape?: DataType;
    responseShape?: DataType;
    description?: string;
    expectedHeaders?: string[];
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'OPTIONS' | 'ANY';

const METHODS_WITH_BODY: string[] = ['POST', 'PUT', 'PATCH', 'DELETE', 'ANY'];

const headersListType: DataType = {
    kind: 'array',
    subType: {
        kind: 'object',
        properties: { name: { kind: 'string' }, value: { kind: 'string' } },
        restriction: 'exact',
    },
};

function mergeExpectedHeaders(apiHeaders: string[], endpointHeaders: string[]): string[] {
    const merged = new Map<string, string>();
    for (const name of apiHeaders) merged.set(name.toLowerCase(), name);
    for (const name of endpointHeaders) merged.set(name.toLowerCase(), name);
    return Array.from(merged.values());
}

const ALL_METHODS: HttpMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS', 'ANY'];

const PARAM_TYPES: { value: string; label: string }[] = [
    { value: 'string', label: 'string' },
    { value: 'number', label: 'number' },
    { value: 'boolean', label: 'boolean' },
];

// --- Styled ---

const RunNodeButton = styled.button`
    position: absolute;
    top: -40px;
    left: 4px;

    &:hover {
        cursor: pointer;
        opacity: 1;
        ${RainbowBackgroundAnimation}
    }

    opacity: 0.5;
    transition: opacity 0.3s ease;

    box-sizing: border-box;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    outline: none;
    display: flex;
    justify-content: center;
    align-items: center;
    background: ${props => props.theme.backgroundColor};
    border: 2px solid ${props => props.theme.separatorColor};
`;

// --- Helpers ---

const METHOD_COLORS: Record<string, string> = {
    GET: '#22c55e',
    POST: '#3b82f6',
    PUT: '#f59e0b',
    PATCH: '#f97316',
    DELETE: '#ef4444',
    HEAD: '#a855f7',
    OPTIONS: '#6b7280',
    ANY: '#06b6d4',
};

const METHOD_COLORS_LIGHT: Record<string, string> = {
    GET: '#15803d',
    POST: '#1d4ed8',
    PUT: '#b45309',
    PATCH: '#c2410c',
    DELETE: '#dc2626',
    HEAD: '#7c3aed',
    OPTIONS: '#4b5563',
    ANY: '#0e7490',
};

function detectPathParams(path: string): string[] {
    return (path.match(/:([a-zA-Z_][a-zA-Z0-9_]*)/g) || []).map(match => match.slice(1));
}

function getBaseUrl(apiId: string | undefined): string {
    const graphServiceUrl = getEnvironmentConfig().graphServiceUrl;
    const baseUrl = graphServiceUrl.replace(/\/api$/, '');
    if (!apiId) return `${baseUrl}/api/custom/...`;
    return `${baseUrl}/api/custom/${apiId}`;
}

function exampleScalarValue(type: DataType): string {
    switch (type.kind) {
        case 'number':
            return '0';
        case 'boolean':
            return 'true';
        default:
            return 'example';
    }
}

function exampleJsonValue(type: DataType): unknown {
    switch (type.kind) {
        case 'string':
            return '';
        case 'number':
            return 0;
        case 'boolean':
            return true;
        case 'object': {
            const obj: Record<string, unknown> = {};
            const properties = (type as any).properties;
            if (properties) {
                for (const [key, prop] of Object.entries<any>(properties)) {
                    obj[key] = exampleJsonValue(prop.type ?? prop);
                }
            }
            return obj;
        }
        case 'array': {
            const subType = (type as any).subType;
            return subType ? [exampleJsonValue(subType)] : [];
        }
        default:
            return null;
    }
}

function buildCurlCommand(
    baseUrl: string,
    endpoint: EndpointDefinition,
    authMethodName?: AuthMethod,
    apiKeyHeaderName?: string,
    expectedHeaders?: string[],
): string {
    const method = endpoint.method === 'ANY' ? 'GET' : endpoint.method;
    let path = endpoint.path;
    for (const param of endpoint.pathParams) {
        path = path.replace(`:${param.name}`, exampleScalarValue(param.type));
    }

    const queryParts = endpoint.queryParams.map(param => `${param.name}=${encodeURIComponent(exampleScalarValue(param.type))}`);
    const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';

    let curl = `curl -X ${method} "${baseUrl}${path}${queryString}"`;

    // Add auth header based on method
    if (authMethodName === 'basic') {
        curl += ` \\\n  -H "Authorization: Basic {{base64(username:password)}}"`;
    } else if (authMethodName === 'bearer') {
        curl += ` \\\n  -H "Authorization: Bearer {{token}}"`;
    } else if (authMethodName === 'apiKey') {
        const headerName = apiKeyHeaderName || 'X-API-Key';
        curl += ` \\\n  -H "${headerName}: {{api_key}}"`;
    }

    // Add expected headers
    if (expectedHeaders) {
        for (const headerName of expectedHeaders) {
            curl += ` \\\n  -H "${headerName}: {{${headerName.toLowerCase().replace(/-/g, '_')}}}"`;
        }
    }

    if (METHODS_WITH_BODY.includes(endpoint.method)) {
        const bodyJson = JSON.stringify(
            exampleJsonValue(endpoint.requestBodyShape || { kind: 'object', properties: {}, restriction: 'open' }),
            null,
            4,
        );
        curl += ` \\\n  -H "Content-Type: application/json" \\\n  -d '${bodyJson}'`;
    }

    return curl;
}

function dataTypeToLabel(type: DataType): string {
    return type.kind;
}

function makeDefaultEndpoint(endpointNumber: number): EndpointDefinition {
    return {
        id: crypto.randomUUID(),
        method: 'GET',
        path: `/endpoint-${endpointNumber}`,
        description: '',
        streaming: false,
        timeout: 30,
        pathParams: [],
        queryParams: [],
    };
}

const AUTH_METHOD_OPTIONS: { value: AuthMethod; label: string }[] = [
    { value: 'basic', label: 'Basic Auth' },
    { value: 'bearer', label: 'Bearer Token' },
    { value: 'apiKey', label: 'API Key' },
    { value: 'custom', label: 'Custom' },
];

function computeAuthPorts(authFunctionId: string | undefined, functions: FunctionDefinition[]): NodeInstanceDataPort[] {
    if (!authFunctionId) return [];
    const authFunction = functions.find(func => func.id === authFunctionId);
    if (!authFunction) return [];
    return authFunction.outputPortDefinitions
        .filter(portDef => portDef.name !== 'Authenticated')
        .map(portDef => ({ name: `Auth: ${portDef.name}`, type: portDef.type }));
}

function computeEndpointPorts(endpoints: EndpointDefinition[], apiExpectedHeaders: string[]): NodeInstanceDataPort[] {
    const outPorts: NodeInstanceDataPort[] = [];
    for (const endpoint of endpoints) {
        const prefix = `${endpoint.method} ${endpoint.path}`;
        outPorts.push({ name: prefix, type: { kind: 'null' } });
        for (const param of endpoint.pathParams) {
            outPorts.push({ name: `${prefix}: Path: ${param.name}`, type: param.type });
        }
        for (const param of endpoint.queryParams) {
            const portType = param.optional ? createOptional(param.type) : param.type;
            outPorts.push({ name: `${prefix}: Query: ${param.name}`, type: portType });
        }
        // Typed Headers port
        const endpointExpectedHeaders = endpoint.expectedHeaders || [];
        const combinedHeaders = mergeExpectedHeaders(apiExpectedHeaders, endpointExpectedHeaders);
        const headerProperties: Record<string, DataType> = {};
        for (const headerName of combinedHeaders) {
            headerProperties[headerName] = { kind: 'string' };
        }
        outPorts.push({
            name: `${prefix}: Headers`,
            type: { kind: 'object', properties: headerProperties, restriction: 'exact' },
        });
        // Headers List port
        outPorts.push({
            name: `${prefix}: Headers List`,
            type: headersListType,
        });
        if (METHODS_WITH_BODY.includes(endpoint.method)) {
            outPorts.push({
                name: `${prefix}: Body`,
                type: endpoint.requestBodyShape || { kind: 'object', properties: {}, restriction: 'open' },
            });
        }
        if (endpoint.method === 'ANY') {
            outPorts.push({ name: `${prefix}: Method`, type: { kind: 'string' } });
        }
    }
    return outPorts;
}

function createRespondNodeForEndpoint(
    mergeInGraphFn: (nodes: NodeInstance[], connections: DataConnection[], deselectOld?: boolean) => void,
    apiServerNodeId: number,
    apiServerPosition: { x: number; y: number },
    endpointIndex: number,
    triggerPortName: string,
    streaming: boolean,
) {
    const nodeId = streaming ? 'RespondToApiStream' : 'RespondToApi';
    // Use a negative placeholder ID to avoid collisions with real node IDs during
    // mergeInGraph's ID remapping (real IDs are always positive).
    const placeholderNodeId = -1;
    const respondNode = createSingleNodeInstance(placeholderNodeId, nodeId, {
        x: apiServerPosition.x + 850,
        y: apiServerPosition.y + endpointIndex * 200,
    });

    const connection: DataConnection = {
        id: 1,
        sendingNodeInstanceId: apiServerNodeId,
        sendingPortName: triggerPortName,
        sendingPortType: 'external',
        receivingNodeInstanceId: placeholderNodeId,
        receivingPortName: inherentInPortName,
        receivingPortType: 'inherent',
    };

    mergeInGraphFn([respondNode], [connection], false);
}

// --- Section Label ---

const SectionLabel: React.FC<{ children: ReactNode; rightSlot?: ReactNode; labelColor: string }> = ({
    children,
    rightSlot,
    labelColor,
}) => (
    <div className="flex items-center justify-between px-3 py-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: labelColor }}>
            {children}
        </span>
        {rightSlot && <NodeEventInsulator>{rightSlot}</NodeEventInsulator>}
    </div>
);

// --- Constants ---

const AUTH_FUNCTION_PORT_TYPE: DataType = {
    kind: 'function',
    inputTypes: {
        'Authorization Header': { kind: 'string' },
        Headers: {
            kind: 'array',
            subType: {
                kind: 'object',
                properties: { name: { kind: 'string' }, value: { kind: 'string' } },
                restriction: 'exact',
            },
        },
    },
    outputTypes: {
        Authenticated: { kind: 'boolean' },
    },
};

// --- Header Name List Editor ---

const HeaderNameListEditor: React.FC<{
    headers: string[];
    onChange: (headers: string[]) => void;
}> = ({ headers, onChange }) => {
    const [newHeaderName, setNewHeaderName] = useState('');

    const addHeader = () => {
        const trimmed = newHeaderName.trim();
        if (!trimmed) return;
        if (headers.some(header => header.toLowerCase() === trimmed.toLowerCase())) return;
        onChange([...headers, trimmed]);
        setNewHeaderName('');
    };

    const removeHeader = (index: number) => {
        onChange(headers.filter((_header, headerIndex) => headerIndex !== index));
    };

    return (
        <div className="space-y-1">
            {headers.map((header, index) => (
                <div key={index} className="group/row flex items-center gap-2">
                    <span className="text-[11px] font-mono opacity-70 flex-1">{header}</span>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 opacity-0 group-hover/row:opacity-60 hover:!opacity-100 transition-opacity"
                        onClick={() => removeHeader(index)}
                    >
                        <X className="h-3 w-3" />
                    </Button>
                </div>
            ))}
            <div className="flex items-center gap-1">
                <Input
                    className="h-5 text-[10px] flex-1 px-1 font-mono placeholder:text-current placeholder:opacity-50"
                    placeholder="Header-Name"
                    value={newHeaderName}
                    onChange={event => setNewHeaderName(event.target.value)}
                    onKeyDown={event => {
                        if (event.key === 'Enter') addHeader();
                    }}
                />
                <Button variant="outline" size="sm" className="h-5 px-1.5 text-[10px]" onClick={addHeader}>
                    <Plus className="h-3 w-3" />
                </Button>
            </div>
        </div>
    );
};

// --- Props ---

interface Props {
    name: string;
    nodeInstance: NodeInstance;
    nodeState: Record<string, any>;
    setNodeState: (state: NodeInstanceState) => void;
    renderInherentInPort: () => ReactNode;
    renderInherentOutPort: () => ReactNode;
}

// --- Main Component ---

export const ApiServerContentLayout: React.FC<Props> = ({
    name,
    nodeInstance,
    nodeState,
    setNodeState,
    renderInherentInPort,
    renderInherentOutPort,
}) => {
    const triggerRun = useTriggerRun();
    const selected = useNodeSelected();
    const mergeInGraphFn = useMergeInGraph();
    const setNodeShape = useNodeShape();
    const navigateToFunction = useNavigateToFunction();
    const graphDispatch = useGraphDispatch();
    const functions = useFunctions();
    const theme = useContext<ThemeType>(ThemeContext);
    const { isDark } = useTheme();

    const separatorStyle = { borderColor: theme.separatorColor };
    const labelColor = theme.subtleContrastColor;

    // --- graphId + API ---
    const { graphId } = useParams<{ graphId: string }>();
    const { getApisForGraph, createApi: createApiCall, updateApi: updateApiCall } = useApiServerApi();

    // --- State from nodeInstance.state ---
    const apiId = nodeState.apiId as string | undefined;
    const apiName = (nodeState.apiName as string) ?? 'Untitled API';
    const endpoints = (nodeState.endpoints as EndpointDefinition[]) || [];
    const authEnabled = (nodeState.authEnabled as boolean) || false;
    const authMethod = (nodeState.authMethod as AuthMethod) || 'basic';
    const authFunctionId = nodeState.authFunctionId as string | undefined;
    const authHeaderName = (nodeState.authHeaderName as string) || 'X-API-Key';
    const apiExpectedHeaders = (nodeState.apiExpectedHeaders as string[]) || [];

    // --- Local state ---
    const [copiedUrl, setCopiedUrl] = useState(false);
    const [copiedCurlEndpointId, setCopiedCurlEndpointId] = useState<string | null>(null);
    const [editingQueryNames, setEditingQueryNames] = useState<Record<string, string>>({});
    const [apiHeadersExpanded, setApiHeadersExpanded] = useState(false);

    // --- Backend API creation + sync ---
    const nodeStateRef = useRef(nodeState);
    nodeStateRef.current = nodeState;
    const apiRef = useRef({ getApisForGraph, createApiCall, updateApiCall });
    apiRef.current = { getApisForGraph, createApiCall, updateApiCall };

    // Auto-create backend API resource when apiId is missing
    const apiCreationInProgress = useRef(false);
    useEffect(() => {
        if (apiId || !graphId || apiCreationInProgress.current) return;
        apiCreationInProgress.current = true;

        const ensureApi = async () => {
            try {
                const existingApis = await apiRef.current.getApisForGraph(graphId);
                const existingApi = existingApis.find(resource => resource.nodeInstanceId === nodeInstance.id);

                if (existingApi) {
                    setNodeState({ ...nodeStateRef.current, apiId: existingApi.id });
                    apiCreationInProgress.current = false;
                    return;
                }

                const apiName = (nodeStateRef.current.apiName as string) || 'Untitled API';
                const created = await apiRef.current.createApiCall(graphId, nodeInstance.id, apiName);
                setNodeState({ ...nodeStateRef.current, apiId: created.id });
                apiCreationInProgress.current = false;
            } catch {
                apiCreationInProgress.current = false;
            }
        };

        ensureApi();
    }, [apiId, graphId, nodeInstance.id, setNodeState]);

    // Sync to backend (debounced 1s)
    const syncTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    useEffect(() => {
        if (!apiId) return;

        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = setTimeout(() => {
            apiRef.current
                .updateApiCall(apiId, {
                    name: apiName,
                    endpoints: endpoints,
                    expectedHeaders: apiExpectedHeaders,
                    authConfig: authEnabled
                        ? { enabled: true, method: authMethod, headerName: authMethod === 'apiKey' ? authHeaderName : undefined }
                        : null,
                })
                .catch(() => {});
        }, 1000);

        return () => clearTimeout(syncTimeoutRef.current);
    }, [apiId, apiName, endpoints, apiExpectedHeaders, authEnabled, authMethod, authHeaderName]);

    // --- Base URL ---
    const baseUrl = getBaseUrl(apiId);

    // --- Shape update helper ---
    // Computes full port shape including auth inPort + auth output ports (if auth enabled) + endpoint ports.
    const computeFullShape = useCallback(
        (
            currentEndpoints: EndpointDefinition[],
            currentAuthEnabled: boolean,
            currentAuthFunctionId: string | undefined,
            currentFunctions: typeof functions,
            currentApiExpectedHeaders: string[],
        ) => {
            const apiServerNodeDef = NODE_LIBRARY_DICTIONARY['ApiServer'];
            const baseInPorts = [...apiServerNodeDef.inPorts];
            const authOutPortsList = currentAuthEnabled ? computeAuthPorts(currentAuthFunctionId, currentFunctions) : [];

            if (currentAuthEnabled && currentAuthFunctionId) {
                baseInPorts.push({ name: 'Auth Function', type: AUTH_FUNCTION_PORT_TYPE });
            }

            return {
                inPorts: baseInPorts,
                outPorts: [...authOutPortsList, ...computeEndpointPorts(currentEndpoints, currentApiExpectedHeaders)],
            };
        },
        [],
    );

    // --- Endpoint CRUD ---
    // Helper to persist endpoints in node state. EndpointDefinition[] isn't
    // structurally assignable to DataValue (NodeInstanceState values), but at
    // runtime this is safe — the graph engine serialises state as JSON.
    const setEndpoints = useCallback(
        (newEndpoints: EndpointDefinition[]) => {
            setNodeState({ ...nodeState, endpoints: newEndpoints } as unknown as NodeInstanceState);
            setNodeShape(computeFullShape(newEndpoints, authEnabled, authFunctionId, functions, apiExpectedHeaders));
        },
        [nodeState, setNodeState, setNodeShape, computeFullShape, authEnabled, authFunctionId, functions, apiExpectedHeaders],
    );

    const addEndpoint = () => {
        const newEndpoint = makeDefaultEndpoint(endpoints.length + 1);
        const newEndpoints = [...endpoints, newEndpoint];
        setEndpoints(newEndpoints);

        if (mergeInGraphFn) {
            const triggerPortName = `${newEndpoint.method} ${newEndpoint.path}`;
            createRespondNodeForEndpoint(
                mergeInGraphFn,
                nodeInstance.id,
                nodeInstance.position,
                newEndpoints.length - 1,
                triggerPortName,
                newEndpoint.streaming,
            );
        }
    };

    const removeEndpoint = (endpointId: string) => {
        setEndpoints(endpoints.filter(endpoint => endpoint.id !== endpointId));
    };

    const updateEndpoint = useCallback(
        (endpointId: string, updates: Partial<EndpointDefinition>) => {
            const newEndpoints = endpoints.map(endpoint => (endpoint.id === endpointId ? { ...endpoint, ...updates } : endpoint));
            setEndpoints(newEndpoints);
        },
        [endpoints, setEndpoints],
    );

    // --- Path param auto-detection ---
    const handlePathChange = useCallback(
        (endpointId: string, newPath: string) => {
            const endpoint = endpoints.find(endpoint => endpoint.id === endpointId);
            if (!endpoint) return;

            const detectedNames = detectPathParams(newPath);
            const existingByName = new Map(endpoint.pathParams.map(param => [param.name, param]));

            const newPathParams = detectedNames.map(paramName => {
                const existing = existingByName.get(paramName);
                return existing || { name: paramName, type: { kind: 'string' } as DataType };
            });

            updateEndpoint(endpointId, { path: newPath, pathParams: newPathParams });
        },
        [endpoints, updateEndpoint],
    );

    // --- Query param CRUD ---
    const addQueryParam = (endpointId: string) => {
        const endpoint = endpoints.find(endpoint => endpoint.id === endpointId);
        if (!endpoint) return;
        const paramName = '';
        const newParams = [...endpoint.queryParams, { name: paramName, type: { kind: 'string' } as DataType, optional: true }];
        const paramKey = `${endpointId}-${newParams.length - 1}`;
        setEditingQueryNames(prev => ({ ...prev, [paramKey]: '' }));
        updateEndpoint(endpointId, { queryParams: newParams });
    };

    const removeQueryParam = (endpointId: string, paramIndex: number) => {
        const endpoint = endpoints.find(endpoint => endpoint.id === endpointId);
        if (!endpoint) return;
        const newParams = endpoint.queryParams.filter((_param, index) => index !== paramIndex);
        const paramKey = `${endpointId}-${paramIndex}`;
        setEditingQueryNames(prev => {
            const next = { ...prev };
            delete next[paramKey];
            return next;
        });
        updateEndpoint(endpointId, { queryParams: newParams });
    };

    const commitQueryName = (endpointId: string, paramIndex: number) => {
        const paramKey = `${endpointId}-${paramIndex}`;
        const editedName = editingQueryNames[paramKey];
        if (editedName === undefined) return;
        setEditingQueryNames(prev => {
            const next = { ...prev };
            delete next[paramKey];
            return next;
        });

        const endpoint = endpoints.find(endpoint => endpoint.id === endpointId);
        if (!endpoint) return;

        if (!editedName.trim()) {
            // Empty name — remove
            const newParams = endpoint.queryParams.filter((_param, index) => index !== paramIndex);
            updateEndpoint(endpointId, { queryParams: newParams });
        } else {
            const newParams = endpoint.queryParams.map((param, index) => (index === paramIndex ? { ...param, name: editedName } : param));
            updateEndpoint(endpointId, { queryParams: newParams });
        }
    };

    const changeQueryParamType = (endpointId: string, paramIndex: number, newKind: string) => {
        const endpoint = endpoints.find(endpoint => endpoint.id === endpointId);
        if (!endpoint) return;
        const newParams = endpoint.queryParams.map((param, index) =>
            index === paramIndex ? { ...param, type: { kind: newKind } as DataType } : param,
        );
        updateEndpoint(endpointId, { queryParams: newParams });
    };

    const changeQueryParamOptional = (endpointId: string, paramIndex: number, optional: boolean) => {
        const endpoint = endpoints.find(endpoint => endpoint.id === endpointId);
        if (!endpoint) return;
        const newParams = endpoint.queryParams.map((param, index) => (index === paramIndex ? { ...param, optional } : param));
        updateEndpoint(endpointId, { queryParams: newParams });
    };

    const changePathParamType = (endpointId: string, paramName: string, newKind: string) => {
        const endpoint = endpoints.find(endpoint => endpoint.id === endpointId);
        if (!endpoint) return;
        const newParams = endpoint.pathParams.map(param =>
            param.name === paramName ? { ...param, type: { kind: newKind } as DataType } : param,
        );
        updateEndpoint(endpointId, { pathParams: newParams });
    };

    // --- Copy handlers ---
    const copyUrl = () => {
        navigator.clipboard.writeText(baseUrl);
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
    };

    const copyCurl = (endpoint: EndpointDefinition) => {
        const combinedHeaders = mergeExpectedHeaders(apiExpectedHeaders, endpoint.expectedHeaders || []);
        const curl = buildCurlCommand(
            baseUrl,
            endpoint,
            authEnabled ? authMethod : undefined,
            authEnabled && authMethod === 'apiKey' ? authHeaderName : undefined,
            combinedHeaders,
        );
        navigator.clipboard.writeText(curl);
        setCopiedCurlEndpointId(endpoint.id);
        setTimeout(() => setCopiedCurlEndpointId(null), 2000);
    };

    const downloadOpenApiSpec = () => {
        if (!apiId) return;
        const specUrl = `${baseUrl}/openapi.json`;
        fetch(specUrl)
            .then(response => response.json())
            .then(spec => {
                const blob = new Blob([JSON.stringify(spec, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const anchor = document.createElement('a');
                anchor.href = url;
                anchor.download = `${apiName.replace(/\s+/g, '-').toLowerCase()}-openapi.json`;
                anchor.click();
                URL.revokeObjectURL(url);
            })
            .catch(error => {
                console.error('Failed to download OpenAPI spec:', error);
            });
    };

    // --- Authentication handlers ---
    const handleEnableAuth = () => {
        if (!graphDispatch) return;
        const authFunc = createAuthFunction('basic');
        graphDispatch(addFunction(authFunc));
        graphDispatch(setFunctionPortValue(nodeInstance.id, 'Auth Function', authFunc.id));
        const newState = {
            ...nodeState,
            authEnabled: true,
            authMethod: 'basic',
            authFunctionId: authFunc.id,
        } as unknown as NodeInstanceState;
        setNodeState(newState);
        // Use the newly created function directly since it won't be in `functions` context yet
        const newAuthPorts = authFunc.outputPortDefinitions
            .filter(portDef => portDef.name !== 'Authenticated')
            .map(portDef => ({ name: `Auth: ${portDef.name}`, type: portDef.type }));
        const apiServerNodeDef = NODE_LIBRARY_DICTIONARY['ApiServer'];
        setNodeShape({
            inPorts: [...apiServerNodeDef.inPorts, { name: 'Auth Function', type: AUTH_FUNCTION_PORT_TYPE }],
            outPorts: [...newAuthPorts, ...computeEndpointPorts(endpoints, apiExpectedHeaders)],
        });
    };

    const handleDisableAuth = () => {
        if (!graphDispatch) return;
        if (authFunctionId) {
            graphDispatch(deleteFunction(authFunctionId));
            graphDispatch(setFunctionPortValue(nodeInstance.id, 'Auth Function', undefined));
        }
        const newState = {
            ...nodeState,
            authEnabled: false,
            authMethod: undefined,
            authFunctionId: undefined,
        } as unknown as NodeInstanceState;
        setNodeState(newState);
        setNodeShape(computeFullShape(endpoints, false, undefined, functions, apiExpectedHeaders));
    };

    const handleAuthMethodChange = (newMethod: AuthMethod) => {
        if (!graphDispatch) return;
        // Delete old function
        if (authFunctionId) {
            graphDispatch(deleteFunction(authFunctionId));
        }
        // Create new function for the new method
        const authFunc = createAuthFunction(newMethod);
        graphDispatch(addFunction(authFunc));
        graphDispatch(setFunctionPortValue(nodeInstance.id, 'Auth Function', authFunc.id));
        const newState = {
            ...nodeState,
            authMethod: newMethod,
            authFunctionId: authFunc.id,
        } as unknown as NodeInstanceState;
        setNodeState(newState);
        // Use the newly created function directly since it won't be in `functions` context yet
        const newAuthPorts = authFunc.outputPortDefinitions
            .filter(portDef => portDef.name !== 'Authenticated')
            .map(portDef => ({ name: `Auth: ${portDef.name}`, type: portDef.type }));
        const apiServerNodeDef = NODE_LIBRARY_DICTIONARY['ApiServer'];
        setNodeShape({
            inPorts: [...apiServerNodeDef.inPorts, { name: 'Auth Function', type: AUTH_FUNCTION_PORT_TYPE }],
            outPorts: [...newAuthPorts, ...computeEndpointPorts(endpoints, apiExpectedHeaders)],
        });
    };

    const handleEditAuthFunction = () => {
        if (authFunctionId && navigateToFunction) {
            navigateToFunction(authFunctionId);
        }
    };

    // --- Auth output ports for rendering ---
    // Derive auth ports directly from the resolved nodeInstance.outPorts by
    // checking the "Auth: " prefix. This keeps display in sync with the actual
    // node shape rather than relying on a separately-computed list from the
    // functions context (which may update before outPorts are recomputed).
    const authPortsFromInstance = nodeInstance.outPorts.filter(port => port.name.startsWith('Auth: '));
    const authPortCount = authPortsFromInstance.length;

    // --- Port filtering per endpoint ---
    // Ports are generated sequentially per endpoint in computeShape, so slice
    // by index range rather than filtering by prefix (which breaks when
    // multiple endpoints share the same method+path).
    const getEndpointPorts = (endpointIndex: number) => {
        // Auth output ports come before endpoint ports
        let offset = authPortCount;
        for (let previousIndex = 0; previousIndex < endpointIndex; previousIndex++) {
            const previousEndpoint = endpoints[previousIndex];
            offset +=
                3 + // Trigger + Headers + Headers List (always present)
                previousEndpoint.pathParams.length +
                previousEndpoint.queryParams.length +
                (METHODS_WITH_BODY.includes(previousEndpoint.method) ? 1 : 0) +
                (previousEndpoint.method === 'ANY' ? 1 : 0);
        }
        const currentEndpoint = endpoints[endpointIndex];
        const count =
            3 +
            currentEndpoint.pathParams.length +
            currentEndpoint.queryParams.length +
            (METHODS_WITH_BODY.includes(currentEndpoint.method) ? 1 : 0) +
            (currentEndpoint.method === 'ANY' ? 1 : 0);
        return nodeInstance.outPorts.slice(offset, offset + count);
    };

    // --- Render ---

    const renderManualTrigger = () => {
        if (selected) {
            return (
                <RunNodeButton aria-label={`Run block ${name}`} onClick={triggerRun} onPointerDown={event => event.stopPropagation()}>
                    <ArrowIcon />
                </RunNodeButton>
            );
        }
        return null;
    };

    const addButton = (onClick: () => void) => (
        <Button variant="outline" size="sm" className="h-6 w-6 p-0 text-xs" onClick={onClick}>
            +
        </Button>
    );

    return (
        <div style={{ minWidth: 600 }}>
            {renderManualTrigger()}
            {/* Header */}
            <div className="flex justify-between pt-2.5 pb-1">
                {renderInherentInPort()}
                <h2 className="m-0 font-medium text-[1.2em] grow text-left px-2">{name}</h2>
                {renderInherentOutPort()}
            </div>

            {/* Base URL bar */}
            <div className="px-3 pb-2">
                <NodeEventInsulator>
                    <div className="flex items-center gap-1 px-2 py-1 rounded bg-current/5 border border-current/10">
                        <span className="text-[10px] font-semibold uppercase tracking-wider opacity-40 shrink-0">Base URL</span>
                        <span className="text-[10px] truncate opacity-60 flex-1 font-mono">{baseUrl}</span>
                        <Button variant="ghost" size="sm" className="h-5 w-5 p-0 shrink-0" onClick={copyUrl} title="Copy base URL">
                            {copiedUrl ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 shrink-0"
                            onClick={downloadOpenApiSpec}
                            disabled={!apiId}
                            title="Download OpenAPI spec"
                        >
                            <Download className="h-3 w-3" />
                        </Button>
                    </div>
                </NodeEventInsulator>
            </div>

            {/* Authentication section */}
            <div className="border-t" style={separatorStyle}>
                <div className="px-3 py-2">
                    <NodeEventInsulator>
                        <div className="flex items-center gap-3">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="h-3 w-3"
                                    checked={authEnabled}
                                    onChange={event => {
                                        if (event.target.checked) {
                                            handleEnableAuth();
                                        } else {
                                            handleDisableAuth();
                                        }
                                    }}
                                />
                                <Shield className="h-3 w-3 opacity-60" />
                                <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: labelColor }}>
                                    Authentication
                                </span>
                            </label>

                            {authEnabled && (
                                <>
                                    <Select value={authMethod} onValueChange={value => handleAuthMethodChange(value as AuthMethod)}>
                                        <SelectTrigger className="h-6 w-auto min-w-[100px] px-2 py-0.5 text-[10px]">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {AUTH_METHOD_OPTIONS.map(option => (
                                                <SelectItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>

                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-6 px-2 text-[10px]"
                                        onClick={handleEditAuthFunction}
                                        disabled={!authFunctionId}
                                    >
                                        <Pencil className="h-3 w-3 mr-0.5" />
                                        Edit
                                    </Button>
                                </>
                            )}
                        </div>
                    </NodeEventInsulator>

                    {/* API Key header name config */}
                    {authEnabled && authMethod === 'apiKey' && (
                        <NodeEventInsulator>
                            <div className="flex items-center gap-1.5 mt-1.5">
                                <span className="text-[10px] opacity-50 shrink-0">Header Name:</span>
                                <Input
                                    className="h-5 text-[10px] flex-1 px-1 font-mono"
                                    value={authHeaderName}
                                    placeholder="X-API-Key"
                                    onChange={event => {
                                        setNodeState({
                                            ...nodeState,
                                            authHeaderName: event.target.value,
                                        } as unknown as NodeInstanceState);
                                    }}
                                />
                            </div>
                        </NodeEventInsulator>
                    )}
                </div>

                {/* Auth ports — two-column layout like endpoint cards */}
                {authEnabled && (
                    <div className="flex border-t" style={separatorStyle}>
                        {/* Left column — auth function inPort */}
                        <div className="flex-1 min-w-0 pb-1">
                            {authFunctionId && (
                                <>
                                    <SectionLabel labelColor={labelColor}>Auth Function</SectionLabel>
                                    <InPortList ports={nodeInstance.inPorts.filter(port => port.name === 'Auth Function')} />
                                </>
                            )}
                        </div>

                        {/* Right column — auth output ports */}
                        {authPortsFromInstance.length > 0 && (
                            <div className="min-w-[180px] border-l pb-1" style={separatorStyle}>
                                <SectionLabel labelColor={labelColor}>Auth Outputs</SectionLabel>
                                <OutPortList ports={authPortsFromInstance} />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* API-Level Expected Headers */}
            <div className="border-t" style={separatorStyle}>
                <NodeEventInsulator>
                    <button
                        className="flex items-center gap-1 px-3 py-2 w-full bg-transparent border-0 cursor-pointer"
                        onClick={() => setApiHeadersExpanded(!apiHeadersExpanded)}
                    >
                        <ChevronRight
                            className="h-3 w-3 transition-transform"
                            style={{
                                color: labelColor,
                                transform: apiHeadersExpanded ? 'rotate(90deg)' : undefined,
                            }}
                        />
                        <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: labelColor }}>
                            Expected Headers
                        </span>
                        {!apiHeadersExpanded && apiExpectedHeaders.length > 0 && (
                            <span className="text-[10px] font-mono opacity-50 ml-1">
                                {apiExpectedHeaders.length} header{apiExpectedHeaders.length !== 1 ? 's' : ''}
                            </span>
                        )}
                    </button>
                </NodeEventInsulator>
                {apiHeadersExpanded && (
                    <div className="px-3 pb-2">
                        <NodeEventInsulator>
                            <HeaderNameListEditor
                                headers={apiExpectedHeaders}
                                onChange={newHeaders => {
                                    const newState = { ...nodeState, apiExpectedHeaders: newHeaders };
                                    setNodeState(newState as unknown as NodeInstanceState);
                                    setNodeShape(computeFullShape(endpoints, authEnabled, authFunctionId, functions, newHeaders));
                                }}
                            />
                        </NodeEventInsulator>
                    </div>
                )}
            </div>

            {/* Endpoint cards */}
            <div className="border-t flex flex-col gap-5" style={separatorStyle}>
                {endpoints.map((endpoint, endpointIndex) => (
                    <EndpointCard
                        key={endpoint.id}
                        endpoint={endpoint}
                        isDark={isDark}
                        separatorStyle={separatorStyle}
                        labelColor={labelColor}
                        outPorts={getEndpointPorts(endpointIndex)}
                        copiedCurl={copiedCurlEndpointId === endpoint.id}
                        editingQueryNames={editingQueryNames}
                        onMethodChange={method => updateEndpoint(endpoint.id, { method })}
                        onPathChange={path => handlePathChange(endpoint.id, path)}
                        onStreamingChange={streaming => updateEndpoint(endpoint.id, { streaming })}
                        onTimeoutChange={timeout => updateEndpoint(endpoint.id, { timeout })}
                        onDescriptionChange={description => updateEndpoint(endpoint.id, { description })}
                        onRequestBodyShapeChange={shape => updateEndpoint(endpoint.id, { requestBodyShape: shape })}
                        onExpectedHeadersChange={headers => updateEndpoint(endpoint.id, { expectedHeaders: headers })}
                        onPathParamTypeChange={(paramName, kind) => changePathParamType(endpoint.id, paramName, kind)}
                        onAddQueryParam={() => addQueryParam(endpoint.id)}
                        onRemoveQueryParam={index => removeQueryParam(endpoint.id, index)}
                        onCommitQueryName={index => commitQueryName(endpoint.id, index)}
                        onQueryNameChange={(index, value) =>
                            setEditingQueryNames(prev => ({ ...prev, [`${endpoint.id}-${index}`]: value }))
                        }
                        onStartEditingQuery={(index, currentName) =>
                            setEditingQueryNames(prev => ({ ...prev, [`${endpoint.id}-${index}`]: currentName }))
                        }
                        onQueryTypeChange={(index, kind) => changeQueryParamType(endpoint.id, index, kind)}
                        onQueryOptionalChange={(index, optional) => changeQueryParamOptional(endpoint.id, index, optional)}
                        onCopyCurl={() => copyCurl(endpoint)}
                        onAddRespondNode={() => {
                            if (mergeInGraphFn) {
                                const triggerPortName = `${endpoint.method} ${endpoint.path}`;
                                createRespondNodeForEndpoint(
                                    mergeInGraphFn,
                                    nodeInstance.id,
                                    nodeInstance.position,
                                    endpointIndex,
                                    triggerPortName,
                                    endpoint.streaming,
                                );
                            }
                        }}
                        onRemove={() => removeEndpoint(endpoint.id)}
                        addButton={addButton}
                    />
                ))}

                {/* Add Endpoint button */}
                <div className="p-3">
                    <NodeEventInsulator>
                        <button
                            className="w-full py-2 rounded border border-dashed border-current/20 text-[12px] opacity-80 hover:opacity-100 hover:border-current/40 transition-opacity cursor-pointer bg-transparent"
                            onClick={addEndpoint}
                        >
                            <Plus className="h-3 w-3 inline-block mr-1 -mt-0.5" />
                            Add Endpoint
                        </button>
                    </NodeEventInsulator>
                </div>
            </div>
        </div>
    );
};

// --- Endpoint Card ---

interface EndpointCardProps {
    endpoint: EndpointDefinition;
    isDark: boolean;
    separatorStyle: React.CSSProperties;
    labelColor: string;
    outPorts: NodeInstance['outPorts'];
    copiedCurl: boolean;
    editingQueryNames: Record<string, string>;
    onMethodChange: (method: string) => void;
    onPathChange: (path: string) => void;
    onStreamingChange: (streaming: boolean) => void;
    onTimeoutChange: (timeout: number) => void;
    onDescriptionChange: (description: string) => void;
    onRequestBodyShapeChange: (shape: DataType) => void;
    onExpectedHeadersChange: (headers: string[]) => void;
    onPathParamTypeChange: (paramName: string, kind: string) => void;
    onAddQueryParam: () => void;
    onRemoveQueryParam: (index: number) => void;
    onCommitQueryName: (index: number) => void;
    onQueryNameChange: (index: number, value: string) => void;
    onQueryTypeChange: (index: number, kind: string) => void;
    onQueryOptionalChange: (index: number, optional: boolean) => void;
    onStartEditingQuery: (index: number, currentName: string) => void;
    onCopyCurl: () => void;
    onAddRespondNode: () => void;
    onRemove: () => void;
    addButton: (onClick: () => void) => ReactNode;
}

const EndpointCard: React.FC<EndpointCardProps> = ({
    endpoint,
    isDark,
    separatorStyle,
    labelColor,
    outPorts,
    copiedCurl,
    editingQueryNames,
    onMethodChange,
    onPathChange,
    onStreamingChange,
    onTimeoutChange,
    onDescriptionChange,
    onRequestBodyShapeChange,
    onExpectedHeadersChange,
    onPathParamTypeChange,
    onAddQueryParam,
    onRemoveQueryParam,
    onCommitQueryName,
    onQueryNameChange,
    onQueryTypeChange,
    onQueryOptionalChange,
    onStartEditingQuery,
    onCopyCurl,
    onAddRespondNode,
    onRemove,
    addButton,
}) => {
    const colorMap = isDark ? METHOD_COLORS : METHOD_COLORS_LIGHT;
    const methodColor = colorMap[endpoint.method] || '#6b7280';
    const methodBgColor = METHOD_COLORS[endpoint.method] || '#6b7280';
    const hasBody = METHODS_WITH_BODY.includes(endpoint.method);
    const [bodyShapeExpanded, setBodyShapeExpanded] = useState(false);
    const [endpointHeadersExpanded, setEndpointHeadersExpanded] = useState(false);

    return (
        <div className="border-b" style={separatorStyle}>
            {/* Endpoint header */}
            <div className="flex items-center gap-2 px-3 py-2">
                <NodeEventInsulator>
                    <Select value={endpoint.method} onValueChange={onMethodChange}>
                        <SelectTrigger
                            className="h-6 w-auto min-w-[70px] px-2 py-0.5 rounded text-[10px] font-bold font-mono border-0"
                            style={{ backgroundColor: `${methodBgColor}${isDark ? '20' : '18'}`, color: methodColor }}
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {ALL_METHODS.map(method => (
                                <SelectItem key={method} value={method}>
                                    {method}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </NodeEventInsulator>

                <NodeEventInsulator>
                    <Input
                        className="h-6 text-xs font-mono flex-1"
                        value={endpoint.path}
                        placeholder="/path/:param"
                        onChange={event => onPathChange(event.target.value)}
                    />
                </NodeEventInsulator>

                <NodeEventInsulator>
                    <Button variant="outline" size="sm" className="h-6 w-6 p-0 shrink-0" onClick={onCopyCurl} title="Copy curl command">
                        {copiedCurl ? <Check className="h-3 w-3" /> : <Terminal className="h-3 w-3" />}
                    </Button>
                </NodeEventInsulator>

                <div className="flex-1" />

                <NodeEventInsulator>
                    <div className="flex items-center gap-1.5 shrink-0">
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-6 px-2 text-[10px]"
                            onClick={onAddRespondNode}
                            title="Add Respond node"
                        >
                            <Plus className="h-3 w-3 mr-0.5" />
                            Respond
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-6 w-6 p-0 hover:bg-red-500/10 hover:text-red-500 hover:border-red-500/40"
                            onClick={onRemove}
                            title="Remove endpoint"
                        >
                            <X className="h-3 w-3" />
                        </Button>
                    </div>
                </NodeEventInsulator>
            </div>

            {/* Two-column: config left, ports right */}
            <div className="flex border-t" style={separatorStyle}>
                {/* Left column — config */}
                <div className="flex-1 min-w-0">
                    {/* Path Parameters */}
                    {endpoint.pathParams.length > 0 && (
                        <div className="pb-1">
                            <SectionLabel labelColor={labelColor}>Path Parameters</SectionLabel>
                            {endpoint.pathParams.map(param => (
                                <div key={param.name} className="flex items-center gap-2 px-3 py-1">
                                    <span className="text-[11px] font-mono opacity-70">:{param.name}</span>
                                    <NodeEventInsulator>
                                        <Select value={param.type.kind} onValueChange={kind => onPathParamTypeChange(param.name, kind)}>
                                            <SelectTrigger className="h-5 text-[10px] w-auto min-w-[70px]">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {PARAM_TYPES.map(paramType => (
                                                    <SelectItem key={paramType.value} value={paramType.value}>
                                                        {paramType.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </NodeEventInsulator>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Query Parameters */}
                    <div className="border-t pb-1" style={separatorStyle}>
                        <SectionLabel labelColor={labelColor} rightSlot={addButton(onAddQueryParam)}>
                            Query Parameters
                        </SectionLabel>
                        {endpoint.queryParams.map((param, index) => {
                            const paramKey = `${endpoint.id}-${index}`;
                            const isEditing = editingQueryNames[paramKey] !== undefined;
                            return (
                                <div key={paramKey} className="group/row flex items-center gap-2 px-3 py-1">
                                    <div className="flex-1 min-w-0">
                                        {isEditing ? (
                                            <NodeEventInsulator>
                                                <input
                                                    size={1}
                                                    className="h-6 w-full rounded border border-input bg-transparent px-1.5 text-[11px] font-mono placeholder:text-current placeholder:opacity-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                                    placeholder="param name"
                                                    value={editingQueryNames[paramKey] ?? param.name}
                                                    onChange={event => onQueryNameChange(index, event.target.value)}
                                                    onBlur={() => onCommitQueryName(index)}
                                                    onKeyDown={event => {
                                                        if (event.key === 'Enter') onCommitQueryName(index);
                                                    }}
                                                    autoFocus
                                                />
                                            </NodeEventInsulator>
                                        ) : (
                                            <span className="text-[11px] font-mono opacity-70 truncate block">
                                                {param.name || '(unnamed)'}
                                            </span>
                                        )}
                                    </div>
                                    <NodeEventInsulator>
                                        <Select value={param.type.kind} onValueChange={kind => onQueryTypeChange(index, kind)}>
                                            <SelectTrigger className="h-5 text-[10px] w-auto min-w-[70px]">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {PARAM_TYPES.map(paramType => (
                                                    <SelectItem key={paramType.value} value={paramType.value}>
                                                        {paramType.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </NodeEventInsulator>
                                    <NodeEventInsulator>
                                        <label className="flex items-center gap-0.5 cursor-pointer shrink-0" title="Required">
                                            <input
                                                type="checkbox"
                                                className="h-3 w-3"
                                                checked={!param.optional}
                                                onChange={event => onQueryOptionalChange(index, !event.target.checked)}
                                            />
                                            <span className="text-[10px] opacity-50">required</span>
                                        </label>
                                    </NodeEventInsulator>
                                    <NodeEventInsulator>
                                        <div className="flex items-center gap-0.5">
                                            {!isEditing && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 w-6 p-0 shrink-0 opacity-0 group-hover/row:opacity-60 hover:!opacity-100 transition-opacity"
                                                    onClick={() => onStartEditingQuery(index, param.name)}
                                                >
                                                    <Pencil className="h-3 w-3" />
                                                </Button>
                                            )}
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 w-6 p-0 shrink-0 opacity-0 group-hover/row:opacity-60 hover:!opacity-100 transition-opacity"
                                                onClick={() => onRemoveQueryParam(index)}
                                            >
                                                <X className="h-3 w-3" />
                                            </Button>
                                        </div>
                                    </NodeEventInsulator>
                                </div>
                            );
                        })}
                    </div>

                    {/* Expected Headers (per-endpoint) */}
                    <div className="border-t pb-1" style={separatorStyle}>
                        <NodeEventInsulator>
                            <button
                                className="flex items-center gap-1 px-3 py-2 w-full bg-transparent border-0 cursor-pointer"
                                onClick={() => setEndpointHeadersExpanded(!endpointHeadersExpanded)}
                            >
                                <ChevronRight
                                    className="h-3 w-3 transition-transform"
                                    style={{
                                        color: labelColor,
                                        transform: endpointHeadersExpanded ? 'rotate(90deg)' : undefined,
                                    }}
                                />
                                <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: labelColor }}>
                                    Expected Headers
                                </span>
                                {!endpointHeadersExpanded && (endpoint.expectedHeaders?.length ?? 0) > 0 && (
                                    <span className="text-[10px] font-mono opacity-50 ml-1">
                                        {endpoint.expectedHeaders!.length} header
                                        {endpoint.expectedHeaders!.length !== 1 ? 's' : ''}
                                    </span>
                                )}
                            </button>
                        </NodeEventInsulator>
                        {endpointHeadersExpanded && (
                            <div className="px-3 pb-2">
                                <NodeEventInsulator>
                                    <HeaderNameListEditor headers={endpoint.expectedHeaders || []} onChange={onExpectedHeadersChange} />
                                </NodeEventInsulator>
                            </div>
                        )}
                    </div>

                    {/* Request Body (POST/PUT/PATCH/DELETE/ANY only) */}
                    {hasBody && (
                        <div className="border-t pb-1" style={separatorStyle}>
                            <NodeEventInsulator>
                                <button
                                    className="flex items-center gap-1 px-3 py-2 w-full bg-transparent border-0 cursor-pointer"
                                    onClick={() => setBodyShapeExpanded(!bodyShapeExpanded)}
                                >
                                    <ChevronRight
                                        className="h-3 w-3 transition-transform"
                                        style={{
                                            color: labelColor,
                                            transform: bodyShapeExpanded ? 'rotate(90deg)' : undefined,
                                        }}
                                    />
                                    <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: labelColor }}>
                                        Request Body
                                    </span>
                                    {!bodyShapeExpanded && (
                                        <span className="text-[10px] font-mono opacity-50 ml-1">
                                            {endpoint.requestBodyShape ? dataTypeToLabel(endpoint.requestBodyShape) : 'any (open object)'}
                                        </span>
                                    )}
                                </button>
                            </NodeEventInsulator>
                            {bodyShapeExpanded && (
                                <div className="px-3 pb-2">
                                    <div className="bg-background/75 rounded-md p-2">
                                        <NodeEventInsulator>
                                            <ShapeTypeSelectorEditor
                                                value={
                                                    endpoint.requestBodyShape || {
                                                        kind: 'object',
                                                        properties: {},
                                                        restriction: 'open',
                                                    }
                                                }
                                                onChange={onRequestBodyShapeChange}
                                            />
                                        </NodeEventInsulator>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Settings row */}
                    <div className="border-t px-3 py-2 flex items-center gap-3 flex-wrap" style={separatorStyle}>
                        <NodeEventInsulator>
                            <div className="flex items-center gap-1">
                                <span className="text-[10px] opacity-50">Timeout:</span>
                                <Input
                                    className="h-5 w-12 text-[10px] text-center px-1"
                                    type="number"
                                    min={1}
                                    max={300}
                                    value={endpoint.timeout}
                                    onChange={event => {
                                        const value = parseInt(event.target.value, 10);
                                        if (!isNaN(value)) onTimeoutChange(value);
                                    }}
                                />
                                <span className="text-[10px] opacity-50">s</span>
                            </div>
                        </NodeEventInsulator>

                        <NodeEventInsulator>
                            <label className="flex items-center gap-1 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="h-3 w-3"
                                    checked={endpoint.streaming}
                                    onChange={event => onStreamingChange(event.target.checked)}
                                />
                                <span className="text-[10px] opacity-50">Streaming</span>
                            </label>
                        </NodeEventInsulator>

                        <NodeEventInsulator>
                            <div className="flex items-center gap-1 flex-1 min-w-[100px]">
                                <span className="text-[10px] opacity-50 shrink-0">Desc:</span>
                                <Input
                                    className="h-5 text-[10px] flex-1 px-1 placeholder:text-current placeholder:opacity-40"
                                    value={endpoint.description || ''}
                                    placeholder="Description"
                                    onChange={event => onDescriptionChange(event.target.value)}
                                />
                            </div>
                        </NodeEventInsulator>
                    </div>
                </div>

                {/* Right column — output ports */}
                <div className="min-w-[180px] border-l pb-1" style={separatorStyle}>
                    <SectionLabel labelColor={labelColor}>Outputs</SectionLabel>
                    <OutPortList ports={outPorts} />
                </div>
            </div>
        </div>
    );
};
